(() => {
  'use strict';

  if (window.__nbhFbtInitialized) return;
  window.__nbhFbtInitialized = true;

  const ROOT_SELECTOR = '[data-nbh-complementary]';
  const productCache = new Map();

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character]);

  const formatMoney = (cents, currency = 'DKK') => {
    const amount = Number(cents || 0) / 100;
    const hasDecimals = Math.round(amount * 100) % 100 !== 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'da-DK', {
      style: 'currency',
      currency,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(amount);
  };

  const getCurrency = (productInfo) => productInfo?.querySelector('[data-nbh-currency]')?.textContent?.trim() || 'DKK';

  const getCurrentVariants = (productInfo) => {
    try {
      return JSON.parse(productInfo?.querySelector('[data-nbh-variant-data]')?.textContent || '[]');
    } catch (_error) {
      return [];
    }
  };

  const getCurrentVariant = (productInfo) => {
    const variants = getCurrentVariants(productInfo);
    const selectedId = productInfo?.dataset.nbhSelectedVariantId
      || productInfo?.querySelector('form[action*="/cart/add"] [name="id"]')?.value
      || '';
    return variants.find((variant) => String(variant.id) === String(selectedId))
      || variants.find((variant) => variant.available)
      || variants[0]
      || null;
  };

  const getMainQuantity = (productInfo) => {
    const input = productInfo?.querySelector('.nbh-product__quantity [name="quantity"]');
    const min = Math.max(1, Number.parseInt(input?.min || input?.dataset?.min || '1', 10) || 1);
    return Math.max(min, Number.parseInt(input?.value || String(min), 10) || min);
  };

  const getCurrentImage = (productInfo, variant) => variant?.featured_image?.src
    || variant?.featured_media?.preview_image?.src
    || productInfo?.querySelector('.product__media img, .nbh-product__media img')?.currentSrc
    || productInfo?.querySelector('.product__media img, .nbh-product__media img')?.src
    || '';

  const productJsonUrl = (href) => {
    const url = new URL(href, window.location.origin);
    return `${url.pathname.replace(/\.js$/, '')}.js`;
  };

  const loadProduct = async (href) => {
    const url = productJsonUrl(href);
    if (!productCache.has(url)) {
      productCache.set(url, fetch(url, { headers: { Accept: 'application/json' } }).then(async (response) => {
        if (!response.ok) throw new Error(`Kunne ikke hente produkt (${response.status})`);
        return response.json();
      }));
    }
    return productCache.get(url);
  };

  const selectedRecommendationVariant = (row) => {
    const select = row.querySelector('[data-nbh-fbt-variant-select]');
    const variants = row._nbhVariants || [];
    const id = select?.value || row.dataset.variantId || '';
    return variants.find((variant) => String(variant.id) === String(id)) || variants[0] || null;
  };

  const updateRecommendationRow = (row, currency) => {
    const variant = selectedRecommendationVariant(row);
    if (!variant) return;
    row.dataset.variantId = variant.id;

    const price = row.querySelector('[data-nbh-fbt-price]');
    if (price) price.textContent = formatMoney(variant.price, currency);

    const variantLabel = row.querySelector('[data-nbh-fbt-variant-label]');
    if (variantLabel) variantLabel.textContent = variant.title && variant.title !== 'Default Title' ? variant.title : '';

    const image = row.querySelector('.nbh-fbt__image');
    const nextImage = variant.featured_image?.src || variant.featured_image || '';
    if (image && nextImage && image.src !== nextImage) image.src = nextImage;
  };

  const currentRowMarkup = (productInfo, currency) => {
    const variant = getCurrentVariant(productInfo);
    if (!variant) return '';
    const title = productInfo?.querySelector('[data-nbh-product-title]')?.textContent?.trim() || document.querySelector('h1')?.textContent?.trim() || 'Dette produkt';
    const image = getCurrentImage(productInfo, variant);
    const variantText = variant.title && variant.title !== 'Default Title' ? variant.title : 'Valgt variant';

    return `
      <div class="nbh-fbt__item" data-nbh-fbt-row data-nbh-fbt-current data-variant-id="${escapeHtml(variant.id)}">
        <label class="nbh-fbt__check" aria-label="Dette produkt er med i sættet">
          <input type="checkbox" checked disabled>
        </label>
        ${image ? `<img class="nbh-fbt__image" src="${escapeHtml(image)}" alt="" loading="lazy" width="90" height="90">` : '<span class="nbh-fbt__image" aria-hidden="true"></span>'}
        <div class="nbh-fbt__details">
          <strong class="nbh-fbt__title">${escapeHtml(title)}</strong>
          <span class="nbh-fbt__variant" data-nbh-fbt-current-variant>${escapeHtml(variantText)}</span>
          <span class="nbh-fbt__current-note">Dette produkt</span>
        </div>
        <strong class="nbh-fbt__price" data-nbh-fbt-current-price>${formatMoney(variant.price, currency)}</strong>
      </div>`;
  };

  const recommendationRowMarkup = (product, href, fallbackImage, currency, index) => {
    const candidates = (product.variants || []).filter((variant) => variant.available);
    const variant = candidates[0];
    if (!variant) return '';

    const image = variant.featured_image?.src || variant.featured_image || product.featured_image || fallbackImage || '';
    const variantOptions = candidates.length > 1
      ? `<select class="nbh-fbt__variant-select" data-nbh-fbt-variant-select aria-label="Vælg variant af ${escapeHtml(product.title)}">${candidates.map((candidate) => `<option value="${escapeHtml(candidate.id)}"${candidate.id === variant.id ? ' selected' : ''}>${escapeHtml(candidate.title || 'Variant')} · ${formatMoney(candidate.price, currency)}</option>`).join('')}</select>`
      : `<span class="nbh-fbt__variant" data-nbh-fbt-variant-label>${variant.title && variant.title !== 'Default Title' ? escapeHtml(variant.title) : ''}</span>`;

    return `
      <div class="nbh-fbt__item" data-nbh-fbt-row data-nbh-fbt-recommendation="${index}" data-variant-id="${escapeHtml(variant.id)}">
        <label class="nbh-fbt__check" aria-label="Medtag ${escapeHtml(product.title)}">
          <input type="checkbox" data-nbh-fbt-check>
        </label>
        ${image ? `<img class="nbh-fbt__image" src="${escapeHtml(image)}" alt="" loading="lazy" width="90" height="90">` : '<span class="nbh-fbt__image" aria-hidden="true"></span>'}
        <div class="nbh-fbt__details">
          <a class="nbh-fbt__title" href="${escapeHtml(href)}">${escapeHtml(product.title)}</a>
          ${variantOptions}
        </div>
        <strong class="nbh-fbt__price" data-nbh-fbt-price>${formatMoney(variant.price, currency)}</strong>
      </div>`;
  };

  const syncCurrentRow = (root, productInfo, currency) => {
    const row = root.querySelector('[data-nbh-fbt-current]');
    const variant = getCurrentVariant(productInfo);
    if (!row || !variant) return;

    row.dataset.variantId = variant.id;
    const variantLabel = row.querySelector('[data-nbh-fbt-current-variant]');
    if (variantLabel) variantLabel.textContent = variant.title && variant.title !== 'Default Title' ? variant.title : 'Valgt variant';
    const price = row.querySelector('[data-nbh-fbt-current-price]');
    if (price) price.textContent = formatMoney(variant.price, currency);
    const image = row.querySelector('.nbh-fbt__image');
    const nextImage = getCurrentImage(productInfo, variant);
    if (image && nextImage && image.tagName === 'IMG') image.src = nextImage;
  };

  const updateSummary = (root, currency) => {
    const productInfo = root._nbhProductInfo;
    const mainVariant = getCurrentVariant(productInfo);
    const mainQuantity = getMainQuantity(productInfo);
    let total = 0;
    let count = 0;

    root.querySelectorAll('[data-nbh-fbt-row]').forEach((row) => {
      if (row.hasAttribute('data-nbh-fbt-current')) {
        if (!mainVariant) return;
        total += Number(mainVariant.price || 0) * mainQuantity;
        count += mainQuantity;
        return;
      }

      const check = row.querySelector('[data-nbh-fbt-check]');
      if (!check?.checked) return;

      const variant = selectedRecommendationVariant(row);
      if (!variant?.available) return;

      total += Number(variant.price || 0);
      count += 1;
    });

    const countTarget = root.querySelector('[data-nbh-fbt-count]');
    const totalTarget = root.querySelector('[data-nbh-fbt-total]');
    if (countTarget) countTarget.textContent = `${count} ${count === 1 ? 'vare' : 'varer'} i alt`;
    if (totalTarget) totalTarget.textContent = formatMoney(total, currency);

    const button = root.querySelector('[data-nbh-fbt-add]');
    if (button && !button.hasAttribute('aria-busy')) {
      button.textContent = `Læg ${count} ${count === 1 ? 'vare' : 'varer'} i kurven`;
      button.disabled = !mainVariant?.available || count < 1;
    }
  };

  const collectItems = (root) => {
    const quantities = new Map();
    const productInfo = root._nbhProductInfo;
    const mainVariant = getCurrentVariant(productInfo);
    const mainQuantity = getMainQuantity(productInfo);

    if (mainVariant?.available && mainVariant.id) {
      const id = Number(mainVariant.id);
      quantities.set(id, (quantities.get(id) || 0) + mainQuantity);
    }

    root.querySelectorAll('[data-nbh-fbt-recommendation]').forEach((row) => {
      const check = row.querySelector('[data-nbh-fbt-check]');
      if (!check?.checked) return;

      const variant = selectedRecommendationVariant(row);
      if (!variant?.available || !variant.id) return;

      const id = Number(variant.id);
      quantities.set(id, (quantities.get(id) || 0) + 1);
    });

    return [...quantities.entries()].map(([id, quantity]) => ({ id, quantity }));
  };

  const addBundle = async (root, button) => {
    const error = root.querySelector('[data-nbh-fbt-error]');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    const mainVariant = getCurrentVariant(root._nbhProductInfo);
    if (!mainVariant?.available) {
      if (error) {
        error.textContent = 'Den valgte variant er ikke på lager. Vælg en anden variant og prøv igen.';
        error.hidden = false;
      }
      updateSummary(root, getCurrency(root._nbhProductInfo));
      return;
    }

    const items = collectItems(root);
    if (!items.length) return;

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const originalText = button.textContent;
    button.textContent = 'Lægger i kurven…';

    try {
      const payload = { items };
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement?.(button);
      }

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Produkterne kunne ikke lægges i kurven.');

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'nbh-fbt',
          productVariantId: items[0]?.id,
          cartData: data,
        });
      }

      if (cart?.renderContents && data.sections) cart.renderContents(data);
      else window.location.assign(window.routes?.cart_url || '/cart');
    } catch (caughtError) {
      if (error) {
        error.textContent = caughtError.message || 'Der opstod en fejl. Prøv igen.';
        error.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = originalText;
    }
  };

  const bind = (root, productInfo, currency) => {
    root.addEventListener('change', (event) => {
      const row = event.target.closest?.('[data-nbh-fbt-recommendation]');
      if (row && event.target.matches('[data-nbh-fbt-variant-select]')) updateRecommendationRow(row, currency);
      updateSummary(root, currency);
    });

    root.querySelector('[data-nbh-fbt-add]')?.addEventListener('click', (event) => addBundle(root, event.currentTarget));

    productInfo?.addEventListener('change', () => {
      requestAnimationFrame(() => {
        syncCurrentRow(root, productInfo, currency);
        updateSummary(root, currency);
      });
    });

    productInfo?.addEventListener('input', (event) => {
      if (!event.target?.matches('.nbh-product__quantity [name="quantity"]')) return;
      requestAnimationFrame(() => updateSummary(root, currency));
    });

    const observer = new MutationObserver(() => {
      syncCurrentRow(root, productInfo, currency);
      updateSummary(root, currency);
    });
    if (productInfo) {
      observer.observe(productInfo, {
        attributes: true,
        attributeFilter: ['data-nbh-selected-variant-id'],
      });
    }
    root._nbhCurrentObserver = observer;
  };

  const loadRecommendationSection = async (root, intent) => {
    const url = new URL(root.dataset.url, window.location.origin);
    url.searchParams.set('intent', intent);
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Recommendations unavailable');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return html.querySelector(ROOT_SELECTOR)?.querySelector('.nbh-product__fbt-items')?.closest('.nbh-product__fbt-card') || null;
  };

  const hydrate = async (root) => {
    if (!root || root.dataset.nbhFbtReady || root.dataset.nbhFbtLoading === 'true') return;
    root.dataset.nbhFbtLoading = 'true';
    let card = root.querySelector('.nbh-product__fbt-items')?.closest('.nbh-product__fbt-card');
    try {
      if (!card) {
        // Start fallback at the same time; configured complementary products retain priority.
        const related = loadRecommendationSection(root, 'related').catch(() => null);
        card = await loadRecommendationSection(root, 'complementary').catch(() => null);
        if (!card) card = await related;
      }
      if (!card) {
        root.dataset.nbhFbtReady = 'empty';
        (root.querySelector('[data-nbh-fbt-status]') || root).textContent = 'Ingen forslag til dette produkt lige nu.';
        return;
      }
    } catch (_error) {
      delete root.dataset.nbhFbtLoading;
      root.dataset.nbhFbtReady = 'error';
      (root.querySelector('[data-nbh-fbt-status]') || root).textContent = 'Forslag kunne ikke indlæses lige nu.';
      return;
    }
    const links = [...card.querySelectorAll('.nbh-product__fbt-item')];

    const productInfo = document.querySelector('product-info[data-nbh-product-main]');
    const currency = getCurrency(productInfo);
    root._nbhProductInfo = productInfo;

    try {
      const recommendations = (await Promise.all(links.map(async (link) => {
        try {
          const embedded = link.querySelector('[data-nbh-fbt-product]');
          const product = embedded ? JSON.parse(embedded.textContent) : await loadProduct(link.href);
          return {
            product,
            href: link.href,
            fallbackImage: link.querySelector('img')?.currentSrc || link.querySelector('img')?.src || '',
          };
        } catch (_error) {
          return null;
        }
      }))).filter((item) => item && (item.product?.variants || []).some((variant) => variant.available));

      if (!recommendations.length) {
        root.dataset.nbhFbtReady = 'empty';
        (root.querySelector('[data-nbh-fbt-status]') || root).textContent = 'Ingen tilgængelige forslag lige nu.';
        return;
      }

      const rows = recommendations.map((item, index) => recommendationRowMarkup(item.product, item.href, item.fallbackImage, currency, index)).join('');
      const current = currentRowMarkup(productInfo, currency);

      card.innerHTML = `
        <div class="nbh-fbt__head">
          <h2>Købes ofte sammen</h2>
          <p class="nbh-fbt__subtitle">Forslag der passer godt sammen med denne</p>
        </div>
        <div class="nbh-fbt__items">
          ${current}
          ${rows}
        </div>
        <div class="nbh-fbt__footer">
          <div class="nbh-fbt__summary">
            <span data-nbh-fbt-count></span>
            <strong data-nbh-fbt-total></strong>
          </div>
          <button class="nbh-fbt__button" type="button" data-nbh-fbt-add></button>
        </div>
        <p class="nbh-fbt__error" data-nbh-fbt-error hidden></p>`;

      recommendations.forEach((item, index) => {
        const row = card.querySelector(`[data-nbh-fbt-recommendation="${index}"]`);
        if (!row) return;
        row._nbhVariants = (item.product.variants || []).filter((variant) => variant.available);
        updateRecommendationRow(row, currency);
      });

      root.replaceChildren(card);
      root.dataset.nbhFbtReady = 'true';
      bind(root, productInfo, currency);
      syncCurrentRow(root, productInfo, currency);
      updateSummary(root, currency);
    } finally {
      delete root.dataset.nbhFbtLoading;
    }
  };

  const scan = (scope = document) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) hydrate(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(hydrate);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) scan(node);
    }));

  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
